import React, { useCallback, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
    ReactFlow,
    MiniMap,
    Controls,
    Background,
    addEdge,
    type Connection,
    type Edge,
    type Node,
    type NodeChange,
    type EdgeChange,
    applyNodeChanges,
    applyEdgeChanges,
    Handle,
    Position,
    NodeProps,
    type NodeTypes
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { DocumentDataProvider, useDocumentData, DocumentData } from  '../../context/DocumentDataProvider';
import event_buffer from "../../context/event_def";
import { v4 as uuidv4 } from 'uuid';

export type Port = {
    name: string;
    type: string;
};

export type CustomNodeData = {
    label: string;
    inputs: Port[];
    outputs: Port[];
    custom_data?: { [key: string]: any };
};


// Utility function to extract the last part of a dot-separated string
const getLastSegment = (str: string): string => {
    return str.split('.').pop() || str;
};

// Define a dictionary for type-to-color mapping
const typeColorMap: { [key: string]: string } = {
    default: '#555',
    "Simulador.Optics.BeamPropagation.BeamField": '#c45151ff',
    "System.Double": '#0c549bff',
    "System.Int32" : '#1976d2',
    "System.String": '#388e3c',
    "System.Boolean": '#f9a825',
    // Add more types and their corresponding colors here
};

// Custom node component with focused/selected emphasis
const CustomNode = (props: any) => {
    const data = props.data as CustomNodeData;
    const selected: boolean = !!props.selected;

    const accent = '#e1e1e1ff';
    const baseBg = '#f1f1f1ff';

    return (
        <div style={{
            borderRadius: 10,
            background: baseBg,
            minWidth: 160,
            // height depends on number of ports (inputs/outputs)
            height: (Math.max(data.inputs.length, data.outputs.length) -1 ) * 24 + 20 ,
            minHeight: 24,
            padding: 8,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: selected ? '0 12px 34px rgba(60,60,120,0.18)' : '0 6px 18px rgba(20,20,40,0.08)',
            border: selected ? `3px solid ${accent}` : '2px solid rgba(15,15,30,0.06)',
            transform: selected ? 'scale(1.02)' : 'scale(1)',
            transition: 'box-shadow 160ms ease, transform 160ms ease, border 160ms ease',
            overflow: 'visible',
            fontFamily: 'Inter, Roboto, Arial, sans-serif'
        }}>

            {/* Entradas */}
            {data.inputs.map((input: Port, idx: number) => (
                <Handle
                    key={`in-${idx}`}
                    type="target"
                    position={Position.Left}
                    id={input.name}
                    style={{
                        top: 20 + idx * 24,
                        left: -10,
                        background: typeColorMap[input.type] || '#555',
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        border: '2px solid #fff',
                        boxShadow: '0 6px 16px rgba(20,20,40,0.08)'
                    }}
                    data-type={input.type}
                >
                    <div style={{ position: 'absolute', left: -170, top: -6, fontSize: 11, textAlign: 'right', color: '#222', width: 160 }}>
                        <span style={{ fontWeight: 700 }}>{input.name}</span>
                        <span style={{ marginLeft: 8, color: '#666', fontSize: 10 }}>({getLastSegment(input.type)})</span>
                    </div>
                </Handle>
            ))}

            {/* Salidas */}
            {data.outputs.map((output: Port, idx: number) => (
                <Handle
                    key={`out-${idx}`}
                    type="source"
                    position={Position.Right}
                    id={output.name}
                    style={{
                        top: 20 + idx * 24,
                        right: -10,
                        background: typeColorMap[output.type] || accent,
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        border: '2px solid #fff',
                        boxShadow: '0 6px 16px rgba(20,20,40,0.08)'
                    }}
                    data-type={output.type}
                >
                    <div style={{ position: 'absolute', right: -170, top: -6, fontSize: 11, textAlign: 'left', color: '#222', width: 160 }}>
                        <span style={{ fontWeight: 700 }}>{output.name}</span>
                        <span style={{ marginLeft: 8, color: '#666', fontSize: 10 }}>({getLastSegment(output.type)})</span>
                    </div>
                </Handle>
            ))}

            <div style={{ textAlign: 'center', fontWeight: 700, color: '#111' }}>{data.label}</div>
        </div>
    );
};

const nodeTypes = {
    custom: CustomNode,
};

interface ReactFlowTabProps {
    nodes: Node<CustomNodeData>[];
    setNodes: React.Dispatch<React.SetStateAction<Node<CustomNodeData>[]>>;
    edges: Edge[];
    setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
    selectedNodeIndex: number | null;
    setSelectedNodeIndex: React.Dispatch<React.SetStateAction<number | null>>;
}

const ReactFlowTab : React.FC<ReactFlowTabProps> = ({nodes, setNodes, edges, setEdges, selectedNodeIndex, setSelectedNodeIndex}) => {

    // Grid size for snapping (pixels)
    const GRID_SIZE = 20;
    const onNodesChange = useCallback(
        (changes: NodeChange<Node<CustomNodeData>>[]) => {
            // Snap any position changes to the grid before applying
            const snappedChanges = changes.map((change) => {
                // Some change entries carry a 'position' when nodes are dragged/moved
                const pos = (change as any).position;
                if (pos && typeof pos.x === 'number' && typeof pos.y === 'number') {
                    const snapped = {
                        x: Math.round(pos.x / GRID_SIZE) * GRID_SIZE,
                        y: Math.round(pos.y / GRID_SIZE) * GRID_SIZE,
                    };
                    return { ...change, position: snapped };
                }
                return change;
            });

            setNodes((nodesSnapshot) =>
                applyNodeChanges<Node<CustomNodeData>>(snappedChanges, nodesSnapshot)
            );
            // console.log('Nodos actualizados (snapped):', snappedChanges);
        },
        [setNodes]
    );

    const onEdgesChange = useCallback(
        (changes: EdgeChange[]) => {
            setEdges((edgesSnapshot : Edge[]) => {
                // first apply the raw changes
                let updated = applyEdgeChanges(changes, edgesSnapshot);
                // map styles so selected edges are emphasized
                updated = updated.map((e) => {
                    const edgeAny = e as any;
                    const isSelected = !!edgeAny.selected;
                    const baseColor = (edgeAny.style && edgeAny.style.stroke) || '#6a6cff';
                    if (isSelected) {
                        return {
                            ...e,
                            style: {
                                ...(edgeAny.style || {}),
                                stroke: baseColor,
                                strokeWidth: 3,
                                strokeLinecap: 'round',
                                filter: 'drop-shadow(0 8px 20px rgba(106,108,255,0.24))',
                                opacity: 1
                            }
                        } as any;
                    }
                    // non-selected: subtle style
                    return {
                        ...e,
                        style: {
                            ...(edgeAny.style || {}),
                            // strokeWidth: (edgeAny.style && edgeAny.style.strokeWidth) || 2,
                            strokeWidth: 2,
                            opacity: 0.9,
                        }
                    } as any;
                });

                return updated;
            });
        },
        [setEdges],
    );

    // Solo permite una conexión por entrada (target handle)
    const onConnect = useCallback(
        (params: Connection) => {
            setEdges((eds) => {
                const sourceNode = nodes.find((n) => n.id === params.source);
                const targetNode = nodes.find((n) => n.id === params.target);

                if (sourceNode && targetNode) {
                    const sourcePort = sourceNode.data.outputs.find((o) => o.name === params.sourceHandle);
                    const targetPort = targetNode.data.inputs.find((i) => i.name === params.targetHandle);

                    if (sourcePort?.type !== targetPort?.type) {
                        console.error('Tipo incompatible:', sourcePort?.type, targetPort?.type);
                        return eds; // No agregar la conexión
                    }

                    const alreadyConnected = eds.some(
                        (e) =>
                            e.target === params.target &&
                            e.targetHandle === params.targetHandle
                    );
                    if (!alreadyConnected) {
                        const edgeColor = typeColorMap[sourcePort?.type || 'default'] || '#000';
                        return addEdge({ ...params, style: { stroke: edgeColor, strokeWidth: 2 } }, eds);
                    }
                }
                return eds;
            });
            console.log('Conexión creada:', params);
        },
        [setEdges, nodes]
    );

    // Usar nodes actualizado en el callback
    const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
        const nodeIndex = nodes.findIndex(n => n.id === node.id);
        setSelectedNodeIndex(nodeIndex !== -1 ? nodeIndex : null);
    }, [nodes]);

    return (
        <div style={{ height: '100%', width: '100%', display: 'flex' }}>
            <div style={{ flex: 1 }}>
                <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onNodeClick={onNodeClick}
                        nodeTypes={nodeTypes}
                        fitView
                    >
                    <MiniMap pannable zoomable position={'top-left'} style={{ width: 80, height: 60 }} />
                    <Controls />
                    <Background />
                </ReactFlow>
            </div>
        </div>
    );
};

export default ReactFlowTab;
